import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { customerService } from '../services/customerService';
import omnichannelService from '../services/omnichannelService';
import type {
  CustomerWithRelations,
  CustomerContact,
  CustomerAddress,
  CustomerSocialProfile,
  UpdateCustomerDto
} from '../types/customer360';
import {
  ArrowLeft,
  User,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader,
  ExternalLink,
  Mail,
  MapPin,
  Users,
  Activity,
  Tag as TagIcon,
  Building,
  Briefcase,
  Globe,
  Edit,
  Plus,
  Trash2,
  Settings,
  Ticket,
  MessageSquare,
  MailIcon,
  MessageCircleMoreIcon
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import ActivityTimeline from '../components/customer360/ActivityTimeline';
import TagManager from '../components/customer360/TagManager';
import ContactModal from '../components/customer360/ContactModal';
import AddressModal from '../components/customer360/AddressModal';
import SocialProfileModal from '../components/customer360/SocialProfileModal';
import CustomFieldsForm from '../components/customer360/CustomFieldsForm';
import DeleteConfirmationModal from '../components/customer360/DeleteConfirmationModal';
import CustomerProfileModal from '../components/customer360/CustomerProfileModal';

type TabType = 'overview' | 'contacts' | 'addresses' | 'social' | 'activities' | 'tags' | 'tickets' | 'conversations' | 'custom_fields';

export default function CustomerDetailPage360() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();

  const [customer, setCustomer] = useState<CustomerWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCustomerData();
    }
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const data = await customerService.getCustomerFull(parseInt(id!));
      setCustomer(data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      showToast('Failed to load customer details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLifecycleColor = (stage: string) => {
    switch (stage) {
      case 'lead':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      case 'prospect':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'customer':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'loyal':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'at_risk':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'churned':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const handleSaveCustomerProfile = async (updates: UpdateCustomerDto) => {
    if (!customer) return;

    try {
      await customerService.updateCustomer(customer.id, updates);
      showToast('Customer profile updated successfully', 'success');
      setIsEditProfileOpen(false);
      await fetchCustomerData();
    } catch (error) {
      console.error('Error updating customer profile:', error);
      showToast('Failed to update customer profile', 'error');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Container className="py-6">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader className={`h-8 w-8 animate-spin mx-auto mb-3 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                Loading customer details...
              </p>
            </div>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <Container className="py-6">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-500" />
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                Customer not found
              </p>
              <button
                onClick={() => navigate('/customers')}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Back to Customers
              </button>
            </div>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: User },
    { id: 'tickets' as const, label: 'Tickets', icon: Ticket },
    { id: 'conversations' as const, label: 'Conversations', icon: MessageSquare },
    { id: 'contacts' as const, label: 'Contacts', icon: Phone, badge: customer.contacts?.length },
    { id: 'addresses' as const, label: 'Addresses', icon: MapPin, badge: customer.addresses?.length },
    { id: 'social' as const, label: 'Social', icon: Users, badge: customer.social_profiles?.length },
    { id: 'activities' as const, label: 'Activities', icon: Activity, badge: customer.recent_activities?.length },
    { id: 'tags' as const, label: 'Tags', icon: TagIcon, badge: customer.tags?.length },
    { id: 'custom_fields' as const, label: 'Custom Fields', icon: Settings }
  ];

  return (
    <DashboardLayout>
      <Container className="py-6 space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/customers')}
          className={`flex items-center gap-2 hover:opacity-70 transition-opacity ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Customers</span>
        </button>

        {/* Customer Header */}
        <div
          className={`p-6 rounded-lg shadow-sm border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                  isDark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                }`}
              >
                {customer.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {customer.full_name || 'Unknown Customer'}
                  </h1>
                  {customer.is_vip && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 text-xs font-medium rounded">
                      VIP
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded capitalize ${getLifecycleColor(customer.lifecycle_stage)}`}>
                    {customer.lifecycle_stage}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Phone className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{customer.phone}</span>
                  </div>
                  {customer.email && (
                    <div className="flex items-center gap-2">
                      <Mail className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{customer.email}</span>
                    </div>
                  )}
                  {customer.company_name && (
                    <div className="flex items-center gap-2">
                      <Building className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{customer.company_name}</span>
                      {customer.job_title && (
                        <>
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>•</span>
                          <Briefcase className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{customer.job_title}</span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Customer since {formatDate(customer.customer_since)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsEditProfileOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Edit className="h-4 w-4" />
              Edit Profile
            </button>
          </div>

          {/* Tags Preview */}
          {customer.tags && customer.tags.length > 0 && (
            <div className={`flex flex-wrap gap-2 mt-4 pt-4 border-t ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}>
              {customer.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-3 py-1 rounded-full text-white text-sm"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div
          className={`rounded-lg shadow-sm overflow-hidden border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          {/* Tab Headers */}
          <div
            className={`flex overflow-x-auto border-b ${
              isDark ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? `border-b-2 border-blue-500 ${isDark ? 'text-white bg-gray-700' : 'text-gray-900 bg-gray-50'}`
                      : `${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        isDark
                          ? 'bg-gray-600 text-gray-300'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab customer={customer} />}
            {activeTab === 'tickets' && customer.id && <TicketsTab customerId={customer.id} />}
            {activeTab === 'conversations' && customer.id && <ConversationsTab customerId={customer.id} />}
            {activeTab === 'contacts' && customer.id && <ContactsTab customerId={customer.id} contacts={customer.contacts || []} onRefresh={fetchCustomerData} />}
            {activeTab === 'addresses' && customer.id && <AddressesTab customerId={customer.id} addresses={customer.addresses || []} onRefresh={fetchCustomerData} />}
            {activeTab === 'social' && customer.id && <SocialTab customerId={customer.id} profiles={customer.social_profiles || []} onRefresh={fetchCustomerData} />}
            {activeTab === 'activities' && customer.id && <ActivityTimeline customerId={customer.id} />}
            {activeTab === 'tags' && customer.id && <TagManager customerId={customer.id} selectedTags={customer.tags || []} onTagsChange={fetchCustomerData} />}
            {activeTab === 'custom_fields' && customer.id && <CustomFieldsForm customerId={customer.id} onSave={fetchCustomerData} />}
          </div>
        </div>
      </Container>
      <CustomerProfileModal
        isOpen={isEditProfileOpen}
        customer={customer}
        onClose={() => setIsEditProfileOpen(false)}
        onSave={handleSaveCustomerProfile}
      />
    </DashboardLayout>
  );
}

// Overview Tab Component
function OverviewTab({ customer }: { customer: CustomerWithRelations }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="space-y-6">
      {/* Health Score & Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className={`p-4 rounded-lg border ${
            isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Health Score
          </div>
          <div className="flex items-center gap-2">
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {customer.health_score}/100
            </div>
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${customer.health_score}%`,
                  backgroundColor: customer.health_score >= 70 ? '#10B981' : customer.health_score >= 40 ? '#F59E0B' : '#EF4444'
                }}
              />
            </div>
          </div>
        </div>

        <div
          className={`p-4 rounded-lg border ${
            isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Customer Type
          </div>
          <div className={`text-2xl font-bold capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {customer.customer_type}
          </div>
        </div>

        <div
          className={`p-4 rounded-lg border ${
            isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Language
          </div>
          <div className={`text-2xl font-bold uppercase ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {customer.preferred_language}
          </div>
        </div>
      </div>

      {/* Additional Info */}
      {customer.notes && (
        <div>
          <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Notes
          </h3>
          <div
            className={`p-4 rounded-lg whitespace-pre-wrap border ${
              isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-900'
            }`}
          >
            {customer.notes}
          </div>
        </div>
      )}

      {/* Important Dates */}
      <div>
        <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Important Dates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {customer.last_contact_date && (
            <div
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Last Contact
              </div>
              <div className={isDark ? 'text-white' : 'text-gray-900'}>
                {new Date(customer.last_contact_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          )}
          {customer.next_follow_up_date && (
            <div
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Next Follow-up
              </div>
              <div className={isDark ? 'text-white' : 'text-gray-900'}>
                {new Date(customer.next_follow_up_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Contacts Tab Component
function ContactsTab({
  customerId,
  contacts,
  onRefresh
}: {
  customerId: number;
  contacts: CustomerContact[];
  onRefresh: () => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CustomerContact | undefined>(undefined);
  const [contactPendingDelete, setContactPendingDelete] = useState<CustomerContact | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);

  const handleAddContact = () => {
    setEditingContact(undefined);
    setIsContactModalOpen(true);
  };

  const handleEditContact = (contact: CustomerContact) => {
    setEditingContact(contact);
    setIsContactModalOpen(true);
  };

  const handleSaveContact = async (contactData: Partial<CustomerContact>) => {
    try {
      if (editingContact) {
        await customerService.updateContact(editingContact.id!, contactData);
        showToast('Contact updated successfully', 'success');
      } else {
        // Remove customer_id and optional fields for creation
        const { customer_id, id, created_at, updated_at, ...dataWithoutCustomerId } = contactData;
        await customerService.createContact(customerId, dataWithoutCustomerId as any);
        showToast('Contact added successfully', 'success');
      }
      setIsContactModalOpen(false);
      setEditingContact(undefined);
      onRefresh();
    } catch (error) {
      console.error('Error saving contact:', error);
      showToast('Failed to save contact', 'error');
    }
  };

  const requestDeleteContact = (contact: CustomerContact) => {
    setContactPendingDelete(contact);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setContactPendingDelete(null);
  };

  const handleDeleteContact = async () => {
    if (!contactPendingDelete) return;

    try {
      setIsDeletingContact(true);
      await customerService.deleteContact(contactPendingDelete.id!);
      showToast('Contact deleted successfully', 'success');
      onRefresh();
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting contact:', error);
      showToast('Failed to delete contact', 'error');
    } finally {
      setIsDeletingContact(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Contact Methods
        </h3>
        <button
          onClick={handleAddContact}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Contact
        </button>
      </div>

      {contacts.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No contact methods added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                {contact.contact_type === 'email' || contact.contact_type === 'work_phone' ? (
                  <Mail className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                ) : (
                  <Phone className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>{contact.contact_value}</span>
                    {contact.is_primary && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-xs rounded">
                        Primary
                      </span>
                    )}
                    {contact.verified && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <div className={`text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {contact.contact_type.replace('_', ' ')}
                    {contact.label && ` • ${contact.label}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEditContact(contact)}
                  className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Edit contact"
                >
                  <Edit className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
                <button
                  onClick={() => requestDeleteContact(contact)}
                  className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete contact"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setEditingContact(undefined);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
        customerId={customerId}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen && !!contactPendingDelete}
        title="Delete Contact"
        description={
          contactPendingDelete ? (
            <>
              Are you sure you want to delete the contact <strong>{contactPendingDelete.contact_value}</strong>?
              This will remove it from the customer's contact methods.
            </>
          ) : null
        }
        confirmLabel="Delete Contact"
        isProcessing={isDeletingContact}
        onCancel={() => {
          if (isDeletingContact) return;
          closeDeleteModal();
        }}
        onConfirm={handleDeleteContact}
        previewBadge={contactPendingDelete ? {
          label: contactPendingDelete.contact_value || contactPendingDelete.contact_type || 'Contact',
          color: '#2563EB'
        } : undefined}
      />
    </div>
  );
}

// Addresses Tab Component
function AddressesTab({
  customerId,
  addresses,
  onRefresh
}: {
  customerId: number;
  addresses: CustomerAddress[];
  onRefresh: () => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | undefined>(undefined);
  const [addressPendingDelete, setAddressPendingDelete] = useState<CustomerAddress | null>(null);
  const [isAddressDeleteModalOpen, setIsAddressDeleteModalOpen] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);

  const handleAddAddress = () => {
    setEditingAddress(undefined);
    setIsAddressModalOpen(true);
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setEditingAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData: Partial<CustomerAddress>) => {
    try {
      if (editingAddress) {
        await customerService.updateAddress(editingAddress.id!, addressData);
        showToast('Address updated successfully', 'success');
      } else {
        // Remove customer_id and optional fields for creation
        const { customer_id, id, created_at, updated_at, ...dataWithoutCustomerId } = addressData;
        await customerService.createAddress(customerId, dataWithoutCustomerId as any);
        showToast('Address added successfully', 'success');
      }
      setIsAddressModalOpen(false);
      setEditingAddress(undefined);
      onRefresh();
    } catch (error) {
      console.error('Error saving address:', error);
      showToast('Failed to save address', 'error');
    }
  };

  const requestDeleteAddress = (address: CustomerAddress) => {
    setAddressPendingDelete(address);
    setIsAddressDeleteModalOpen(true);
  };

  const closeAddressDeleteModal = () => {
    setIsAddressDeleteModalOpen(false);
    setAddressPendingDelete(null);
  };

  const handleDeleteAddress = async () => {
    if (!addressPendingDelete) return;

    try {
      setIsDeletingAddress(true);
      await customerService.deleteAddress(addressPendingDelete.id!);
      showToast('Address deleted successfully', 'success');
      onRefresh();
      closeAddressDeleteModal();
    } catch (error) {
      console.error('Error deleting address:', error);
      showToast('Failed to delete address', 'error');
    } finally {
      setIsDeletingAddress(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Addresses
        </h3>
        <button
          onClick={handleAddAddress}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Address
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No addresses added yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MapPin className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {address.address_type}
                  </span>
                  {address.is_primary && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-xs rounded">
                      Primary
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEditAddress(address)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title="Edit address"
                  >
                    <Edit className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                  <button
                    onClick={() => requestDeleteAddress(address)}
                    className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete address"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
              <div className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                {address.street_address && <div>{address.street_address}</div>}
                <div>
                  {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
                </div>
                {address.country && <div>{address.country}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false);
          setEditingAddress(undefined);
        }}
        onSave={handleSaveAddress}
        address={editingAddress}
        customerId={customerId}
      />
      <DeleteConfirmationModal
        isOpen={isAddressDeleteModalOpen && !!addressPendingDelete}
        title="Delete Address"
        description={
          addressPendingDelete ? (
            <>
              Are you sure you want to delete the <strong>{addressPendingDelete.address_type}</strong> address?
              This will remove it from the customer's profile.
            </>
          ) : null
        }
        confirmLabel="Delete Address"
        isProcessing={isDeletingAddress}
        onCancel={() => {
          if (isDeletingAddress) return;
          closeAddressDeleteModal();
        }}
        onConfirm={handleDeleteAddress}
        previewBadge={addressPendingDelete ? {
          label: addressPendingDelete.address_type || 'Address',
          color: '#9333EA'
        } : undefined}
      />
    </div>
  );
}

// Social Tab Component
function SocialTab({
  customerId,
  profiles,
  onRefresh
}: {
  customerId: number;
  profiles: CustomerSocialProfile[];
  onRefresh: () => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<CustomerSocialProfile | undefined>(undefined);
  const [profilePendingDelete, setProfilePendingDelete] = useState<CustomerSocialProfile | null>(null);
  const [isSocialDeleteModalOpen, setIsSocialDeleteModalOpen] = useState(false);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);

  const handleAddProfile = () => {
    setEditingProfile(undefined);
    setIsSocialModalOpen(true);
  };

  const handleEditProfile = (profile: CustomerSocialProfile) => {
    setEditingProfile(profile);
    setIsSocialModalOpen(true);
  };

  const handleSaveProfile = async (profileData: Partial<CustomerSocialProfile>) => {
    try {
      if (editingProfile) {
        await customerService.updateSocialProfile(editingProfile.id!, profileData);
        showToast('Social profile updated successfully', 'success');
      } else {
        // Remove customer_id and optional fields for creation
        const { customer_id, id, created_at, updated_at, ...dataWithoutCustomerId } = profileData;
        await customerService.createSocialProfile(customerId, dataWithoutCustomerId as any);
        showToast('Social profile added successfully', 'success');
      }
      setIsSocialModalOpen(false);
      setEditingProfile(undefined);
      onRefresh();
    } catch (error) {
      console.error('Error saving social profile:', error);
      showToast('Failed to save social profile', 'error');
    }
  };

  const requestDeleteProfile = (profile: CustomerSocialProfile) => {
    setProfilePendingDelete(profile);
    setIsSocialDeleteModalOpen(true);
  };

  const closeSocialDeleteModal = () => {
    setIsSocialDeleteModalOpen(false);
    setProfilePendingDelete(null);
  };

  const handleDeleteProfile = async () => {
    if (!profilePendingDelete) return;

    try {
      setIsDeletingProfile(true);
      await customerService.deleteSocialProfile(profilePendingDelete.id!);
      showToast('Social profile deleted successfully', 'success');
      onRefresh();
      closeSocialDeleteModal();
    } catch (error) {
      console.error('Error deleting social profile:', error);
      showToast('Failed to delete social profile', 'error');
    } finally {
      setIsDeletingProfile(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
      case 'twitter':
      case 'linkedin':
      case 'instagram':
      case 'youtube':
      case 'tiktok':
        return Globe;
      default:
        return Globe;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Social Profiles
        </h3>
        <button
          onClick={handleAddProfile}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Profile
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No social profiles added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map((profile) => {
            const PlatformIcon = getPlatformIcon(profile.platform);
            return (
              <div
                key={profile.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <PlatformIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <div>
                    <div className={`font-medium capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {profile.platform === 'other' ? 'Other Platform' : profile.platform}
                    </div>
                    {profile.profile_handle && (
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        @{profile.profile_handle}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {profile.profile_url && (
                    <a
                      href={profile.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      title="Visit profile"
                    >
                      <ExternalLink className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </a>
                  )}
                  <button
                    onClick={() => handleEditProfile(profile)}
                    className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title="Edit profile"
                  >
                    <Edit className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                  <button
                    onClick={() => requestDeleteProfile(profile)}
                    className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete profile"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SocialProfileModal
        isOpen={isSocialModalOpen}
        onClose={() => {
          setIsSocialModalOpen(false);
          setEditingProfile(undefined);
        }}
        onSave={handleSaveProfile}
        profile={editingProfile}
        customerId={customerId}
      />
      <DeleteConfirmationModal
        isOpen={isSocialDeleteModalOpen && !!profilePendingDelete}
        title="Delete Social Profile"
        description={
          profilePendingDelete ? (
            <>
              Are you sure you want to delete the <strong>{profilePendingDelete.platform}</strong> profile?
              This will remove it from the customer's social list.
            </>
          ) : null
        }
        confirmLabel="Delete Profile"
        isProcessing={isDeletingProfile}
        onCancel={() => {
          if (isDeletingProfile) return;
          closeSocialDeleteModal();
        }}
        onConfirm={handleDeleteProfile}
        previewBadge={profilePendingDelete ? {
          label: profilePendingDelete.profile_handle || profilePendingDelete.platform || 'Profile',
          color: '#0EA5E9'
        } : undefined}
      />
    </div>
  );
}

// Tickets Tab Component
function TicketsTab({
  customerId
}: {
  customerId: number;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalTickets, setTotalTickets] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchCustomerTickets = async () => {
      setLoading(true);
      try {
        const response = await customerService.getCustomerTickets(customerId, {
          limit: 50,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });

        if (!isMounted) {
          return;
        }

        setTickets(response.tickets || []);
        setTotalTickets(response.total ?? response.tickets?.length ?? 0);
      } catch (error) {
        console.error('Error fetching customer tickets:', error);
        if (isMounted) {
          showToast('Failed to load customer tickets', 'error');
          setTickets([]);
          setTotalTickets(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCustomerTickets();

    return () => {
      isMounted = false;
    };
  }, [customerId, showToast]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'text-red-500';
      case 'high':
        return 'text-orange-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-green-500';
      default:
        return isDark ? 'text-gray-400' : 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Customer Tickets
        </h3>
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Total: {totalTickets}
        </div>
      </div>

      {tickets.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Ticket className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No tickets found for this customer</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      #{ticket.id} - {ticket.subject}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                    {ticket.priority && (
                      <span className={`text-xs font-medium capitalize ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority} Priority
                      </span>
                    )}
                    {ticket.category && (
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {ticket.category}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {new Date(ticket.created_at).toLocaleDateString()}
                </div>
              </div>
              
              {ticket.description && (
                <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {ticket.description}
                </p>
              )}
              
              {ticket.assigned_to_name && (
                <div className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Assigned to: {ticket.assigned_to_name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Conversations Tab Component
function ConversationsTab({
  customerId
}: {
  customerId: number;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchCustomerConversations = async () => {
      setLoading(true);
      try {
        const data = await omnichannelService.getConversationsByCustomerId(customerId);
        
        if (!isMounted) return;
        setConversations(data || []);
      } catch (error) {
        console.error('Error fetching customer conversations:', error);
        if (isMounted) {
          showToast('Failed to load customer conversations', 'error');
          setConversations([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCustomerConversations();

    return () => {
      isMounted = false;
    };
  }, [customerId, showToast]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'processing':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Customer Conversations
        </h3>
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Total: {conversations.length}
        </div>
      </div>

      {conversations.length === 0 ? (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No conversations found for this customer</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              onClick={() => navigate(`/atrium`, { state: { selectedConversationId: conv.id } })}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div>
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {conv.subject || 'No Subject'}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${getStatusColor(conv.status)}`}
                      >
                        {conv.status}
                      </span>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {conv.channel_name}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {new Date(conv.updated_at).toLocaleDateString()}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </div>

              {conv.last_message && (
                <p
                  className={`text-sm line-clamp-1 space-x-1 mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  <span>Last Message:</span>
                  <span className="font-medium rounded capitalize bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5">
                    {conv.last_message.sender_type === 'customer' ? 'Customer' : 'Agent'}
                  </span>
                  <span>{conv.last_message.content}</span>
                </p>
              )}

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {conv.message_count} messages
                  </span>
                </div>
                {conv.assigned_agent_name ? (
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Assigned to: {conv.assigned_agent_name}
                  </div>
                ) : (
                  <div className="text-xs text-orange-500 font-medium">Unassigned</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
